import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Flame, MapPin, Eye, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import { CATEGORY_COLORS, CATEGORY_ICONS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrendingToday() {
  const { lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/trending/today`).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data?.location) return null;
  const T = lang === "fr" ? FR : EN;
  const loc = data.location;
  const color = CATEGORY_COLORS[loc.category] || "#8b6f3d";
  const icon = CATEGORY_ICONS[loc.category] || "✨";
  const name = loc.name?.[lang] || loc.name?.fr;
  const desc = loc.description?.[lang] || loc.description?.fr;

  return (
    <section className="relative border-t border-zinc-200 bg-white px-6 md:px-12 py-16" data-testid="trending-today-section">
      <div className="max-w-6xl mx-auto">
        <div className="mb-4 flex items-center gap-3 text-[#8b6f3d]">
          <Flame size={16} className="text-orange-500" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">{T.tag}</span>
        </div>
        <h2 className="font-serif-mystery text-3xl md:text-4xl text-zinc-900 mb-8">
          🔥 {T.title}<br /><span className="italic text-[#8b6f3d]">{T.titleItalic}</span>
        </h2>

        <Link
          to={`/map?loc=${loc.id}`}
          data-testid="trending-today-card"
          className="group flex flex-col md:flex-row gap-6 p-6 md:p-8 rounded-2xl border-2 border-[#8b6f3d]/30 bg-gradient-to-br from-amber-50/50 to-white hover:border-[#8b6f3d] hover:shadow-xl transition-all"
        >
          <div
            className="w-full md:w-48 h-40 md:h-40 rounded-xl flex items-center justify-center text-6xl shrink-0"
            style={{ background: `linear-gradient(135deg, ${color}22, ${color}55)` }}
          >
            <span className="drop-shadow-md">{icon}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span
                className="font-mono-data text-[10px] uppercase tracking-widest px-2 py-1 rounded-full text-white"
                style={{ background: color }}
              >
                {loc.category}
              </span>
              {loc.country && (
                <span className="text-xs text-zinc-600 flex items-center gap-1">
                  <MapPin size={11} /> {loc.country}
                </span>
              )}
              {data.views > 0 && (
                <span className="text-xs text-orange-600 flex items-center gap-1 font-medium">
                  <Eye size={11} /> {data.views} {T.viewsToday}
                </span>
              )}
            </div>
            <h3 className="font-serif-mystery text-2xl md:text-3xl text-zinc-900 leading-tight mb-2 group-hover:text-[#8b6f3d] transition-colors">
              {name}
            </h3>
            <p className="text-zinc-600 leading-relaxed line-clamp-3 mb-4">{desc}</p>
            <Button className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-10 px-4">
              {T.discover}
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </Link>
      </div>
    </section>
  );
}

const FR = {
  tag: "Tendance du jour",
  title: "Histoire la plus consultée",
  titleItalic: "du jour",
  viewsToday: "vues aujourd'hui",
  discover: "Découvrir sur la carte",
};
const EN = {
  tag: "Trending today",
  title: "Most-viewed story",
  titleItalic: "of the day",
  viewsToday: "views today",
  discover: "Discover on the map",
};
